import React from 'react';
import PropTypes from 'prop-types';
import { IconButton } from '@picsio/ui';
import cn from 'classnames';
import { useSelector } from 'react-redux';
import Icon from '../../Icon';
import localization from '../../../shared/strings';
import processingReasons from './Configs/processingReasons';
import processingReRunAbility from './Configs/processingReRunAbility';
import Tooltip from '../../Tooltip';

const ProcessingItem = (props) => {
  const { item, visible, reRunParsing = () => {} } = props;
  const { processing } = useSelector((state) => state.assets.inProgress);

  return (
    <>
      <Choose>
        {/** for crawling */}
        <When condition={item.status === 'complete' && item.name === 'crawling'}>
          <div className="detailsProcessingItem">
            <div className="detailsProcessingItemIcon">
              <If condition={typeof item.icon === 'object'}>
                {item.icon}
              </If>
              <If condition={typeof item.icon === 'string'}>
                <Icon name={item.icon} />
              </If>
            </div>
            <div className="detailsProcessingItemRow">
              <div className="detailsProcessingItemRowText">
                {localization.PROCESSING[item.name]}
              </div>
              <div className="detailsProcessingItemRowIcon">
                <Icon name="ok" />
              </div>
              <Tooltip content={localization.PROCESSING.rerun[item.name]}>
                <IconButton
                  buttonSize="default"
                  className={cn('detailsProcessingItemButton', {
                    inProgress: processing,
                  })}
                  color="default"
                  component="button"
                  disabled={processing}
                  id={`rerunProcessing${item.name}`}
                  onClick={() => reRunParsing(item.name)}
                  size="md"
                >
                  <Icon name="sync" />
                </IconButton>
              </Tooltip>
            </div>
          </div>
        </When>

        {/** for complete */}
        <When condition={item.status === 'complete' && visible}>
          <div className="detailsProcessingItem">
            <div className="detailsProcessingItemIcon">
              <If condition={typeof item.icon === 'object'}>
                {item.icon}
              </If>
              <If condition={typeof item.icon === 'string'}>
                <Icon name={item.icon} />
              </If>
            </div>
            <div className="detailsProcessingItemRow">
              <div className="detailsProcessingItemRowText">
                {localization.PROCESSING[item.name]}
              </div>
              <div className="detailsProcessingItemRowIcon">
                <Icon name="ok" />
              </div>
            </div>
          </div>
        </When>

        <Otherwise>
          <div className="detailsProcessingItem">
            <div className="detailsProcessingItemIcon">
              <If condition={typeof item.icon === 'object'}>
                {item.icon}
              </If>
              <If condition={typeof item.icon === 'string'}>
                <Icon name={item.icon} />
              </If>
            </div>
            <div className="detailsProcessingItemContent">
              <If condition={['waiting', 'running'].includes(item.status)}>
                <div className="detailsProcessingItemRow">
                  <div className="detailsProcessingItemRowText">
                    {localization.PROCESSING[item.name]}
                  </div>
                  <div className="detailsProcessingItemRowIcon">
                    <span className="indicator" />
                  </div>
                </div>
              </If>

              <If condition={['rejected', 'delayed'].includes(item.status)}>
                <div className="detailsProcessingItemText">
                  {item.errors
                    && item.errors.map((error) => {
                      const itemName = item.name.toUpperCase();
                      if (error === 'BY_ACCOUNT_PLAN_LIMITS') {
                        error = `${error}_${itemName}`;
                      }
                      let processingReasonsError = processingReasons[error] || processingReasons.UNKNOWN;
                      if (error === 'BY_FILE_TYPE') {
                        processingReasonsError = processingReasons[`${error}_${itemName}`] || processingReasons[error];
                      }
                      return (
                        <div className="detailsProcessingItemRow" key={error}>
                          <div className="detailsProcessingItemRowText">
                            {processingReasonsError()}
                          </div>
                          <div className="detailsProcessingItemRowIcon">
                            <Icon name="attention" />
                          </div>
                        </div>
                      );
                    })}
                </div>
              </If>

              <If condition={item.status === 'failed'}>
                <Choose>
                  <When condition={item.errorCode}>
                    <div className="detailsProcessingItemText">
                      <div className="detailsProcessingItemRow">
                        <div className="detailsProcessingItemRowText">
                          {processingReasons[item.name]?.[item.errorCode]
                            || processingReasons[item.name]?.UnknownError
                            || localization.PROCESSING.failed[item.name]
                            || localization.PROCESSING[item.name]}
                        </div>
                        <If
                          condition={
                            (item.name === 'metadating'
                              || item.name === 'replicating'
                              || item.name === 'thumbnailing'
                              || item.name === 'converting'
                              || item.name === 'crawling'
                            )
                            && processingReRunAbility[item.name][item.errorCode]
                          }
                        >
                          <Tooltip content={localization.PROCESSING.rerun[item.name]}>
                            <IconButton
                              buttonSize="default"
                              className={cn('detailsProcessingItemButton', {
                                inProgress: processing,
                              })}
                              color="default"
                              component="button"
                              disabled={processing}
                              id={`rerunProcessing${item.name}`}
                              onClick={() => reRunParsing(item.name)}
                              size="md"
                            >
                              <Icon name="sync" />
                            </IconButton>
                          </Tooltip>
                        </If>
                        <div className="detailsProcessingItemRowIcon">
                          <Icon name="close" />
                        </div>
                      </div>
                    </div>
                  </When>
                  <Otherwise>
                    <div className="detailsProcessingItemText">
                      <div className="detailsProcessingItemRow">
                        <div className="detailsProcessingItemRowText">
                          {localization.PROCESSING.failed[item.name]}
                        </div>
                        <div className="detailsProcessingItemRowIcon">
                          <Icon name="close" />
                        </div>
                      </div>
                    </div>
                  </Otherwise>
                </Choose>
              </If>
            </div>
          </div>
        </Otherwise>
      </Choose>
    </>
  );
};

ProcessingItem.propTypes = {
  visible: PropTypes.bool,
  item: PropTypes.object,
  reRunParsing: PropTypes.func,
};

export default ProcessingItem;
